import { useCallback, useMemo, useState } from 'react';

import {
  computeOutcomeKey,
  isEveryQuestionAnswered,
  type OutcomeKey,
  type ReadinessQuestion
} from '@paction-2.0/util/petLossReadiness';

export interface UseReadinessAnswersResult {
  answers: Record<string, string>;
  handleSelect: (questionId: string, value: string) => void;
  handleSubmit: () => boolean;
  handleRestart: () => void;
  isComplete: boolean;
  showResults: boolean;
  outcomeKey: OutcomeKey | null;
}

export function useReadinessAnswers(
  questions: readonly ReadinessQuestion[]
): UseReadinessAnswersResult {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);

  const handleSelect = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const isComplete = useMemo(
    () => isEveryQuestionAnswered(questions, answers),
    [questions, answers]
  );

  const handleSubmit = useCallback(() => {
    if (!isEveryQuestionAnswered(questions, answers)) return false;
    setShowResults(true);
    return true;
  }, [questions, answers]);

  const handleRestart = useCallback(() => {
    setAnswers({});
    setShowResults(false);
  }, []);

  const outcomeKey = useMemo(
    () => (showResults ? computeOutcomeKey(questions, answers) : null),
    [showResults, questions, answers]
  );

  return {
    answers,
    handleSelect,
    handleSubmit,
    handleRestart,
    isComplete,
    showResults,
    outcomeKey
  };
}
